import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import AppleIcon from '@mui/icons-material/Apple';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { getDeviceId, secureSetItem } from '@utils';
import { useConnection } from '@hooks';

interface AppleConnectProps {
  redirect?: string | null;
}

export function AppleConnect(props: Readonly<AppleConnectProps>) {
  const { redirect = null } = props;
  const [disabled, setDisabled] = React.useState(false);
  const { t } = useTranslation('authentication');
  const { getSession } = useConnection();
  const navigate = useNavigate();

  const handleAppleLogin = async () => {
    const deviceId = await getDeviceId();

    if (!deviceId) {
      setDisabled(true);
      import.meta.env.DEV && console.error('Device ID is required for Apple login');
      return;
    }

    const baseUrl = import.meta.env.VITE_OAUTH_API_URI as string;
    const platform = Capacitor.isNativePlatform() ? 'native' : 'web';

    const url = new URL(`${baseUrl}/apple`);
    url.searchParams.set('platform', platform);
    url.searchParams.set('deviceId', deviceId);

    if (redirect) url.searchParams.set('redirect', redirect);

    if (Capacitor.isNativePlatform()) {
      try {
        // Request Apple Sign In
        const { response } = await SignInWithApple.authorize({
          clientId: 'com.festayre.app',
          redirectURI: `${import.meta.env.VITE_OAUTH_API_URI}/apple/native`,
          scopes: 'email name',
          state: deviceId,
          nonce: uuidv4(),
        });

        // Call backend to exchange the identity token for access and refresh tokens
        const result = await fetch(`${import.meta.env.VITE_OAUTH_API_URI}/apple/native`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...response,
            deviceId,
          }),
        });
        const data = await result.json();

        if (data?.accessToken && data?.refreshToken) {
          await secureSetItem('accessToken', data.accessToken);
          await secureSetItem('refreshToken', data.refreshToken);
        } else {
          toast.error(t('toast.error'));
          return;
        }

        await getSession();

        navigate('/', { replace: true });
        return;
      } catch (error) {
        console.error('Apple login failed');
      }
    } else {
      window.location.href = url.toString();
    }
  };

  return (
    <Button
      fullWidth
      variant="contained"
      size="small"
      disabled={disabled}
      onClick={handleAppleLogin}
      sx={{
        bgcolor: 'black',
        color: 'white',
        '&:hover': {
          bgcolor: 'black',
        },
      }}
      startIcon={<AppleIcon />}
    >
      {t('button.Connect with', { provider: 'Apple' })}
    </Button>
  );
}
